import { WindState } from '../types';
import { BOAT_CONFIG } from '../data/boatConfig';

export function renderWindIndicator(
  ctx: CanvasRenderingContext2D,
  wind: WindState,
  x: number,
  y: number,
  size: number = 60,
  isDarkMode: boolean = false
) {
  ctx.save();
  ctx.translate(x, y);

  // Background circle
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fillStyle = isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)';
  ctx.fill();
  ctx.strokeStyle = isDarkMode ? '#444' : '#999';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Cardinal direction labels (N, E, S, W)
  ctx.fillStyle = isDarkMode ? 'rgba(200, 210, 220, 0.8)' : 'rgba(80, 90, 100, 0.8)';
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const labelOffset = size - 12;
  // N (top - heading 0)
  ctx.fillStyle = isDarkMode ? '#e74c3c' : '#c0392b';
  ctx.fillText('N', 0, -labelOffset);
  // S (bottom - heading PI)
  ctx.fillStyle = isDarkMode ? 'rgba(200, 210, 220, 0.7)' : 'rgba(80, 90, 100, 0.7)';
  ctx.fillText('S', 0, labelOffset);
  // E (right - heading PI/2)
  ctx.fillText('E', labelOffset, 0);
  // W (left - heading -PI/2)
  ctx.fillText('W', -labelOffset, 0);

  // Small tick marks for intercardinal directions
  ctx.strokeStyle = isDarkMode ? 'rgba(150, 160, 170, 0.4)' : 'rgba(100, 110, 120, 0.3)';
  ctx.lineWidth = 1;
  const tickInner = size - 8;
  const tickOuter = size - 3;
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI) / 4;
    // Skip cardinal directions (0, 2, 4, 6)
    if (i % 2 === 1) {
      ctx.beginPath();
      ctx.moveTo(Math.sin(angle) * tickInner, -Math.cos(angle) * tickInner);
      ctx.lineTo(Math.sin(angle) * tickOuter, -Math.cos(angle) * tickOuter);
      ctx.stroke();
    }
  }

  // Wind arrow and no-go zone (both rotate with wind direction)
  ctx.rotate(wind.direction);

  // No-go zone indicator (rotates with wind - shows where you CAN'T sail into)
  ctx.beginPath();
  const noGoStart = Math.PI / 2 - BOAT_CONFIG.noGoZoneHalfAngle;
  const noGoEnd = Math.PI / 2 + BOAT_CONFIG.noGoZoneHalfAngle;
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, size - 18, noGoStart, noGoEnd);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 100, 100, 0.3)';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -size + 10);
  ctx.lineTo(-8, 0);
  ctx.lineTo(-3, 0);
  ctx.lineTo(-3, size - 15);
  ctx.lineTo(3, size - 15);
  ctx.lineTo(3, 0);
  ctx.lineTo(8, 0);
  ctx.closePath();

  const arrowGradient = ctx.createLinearGradient(0, -size, 0, size);
  arrowGradient.addColorStop(0, '#3498db');
  arrowGradient.addColorStop(1, '#2980b9');
  ctx.fillStyle = arrowGradient;
  ctx.fill();

  ctx.strokeStyle = '#1a5276';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();

  ctx.save();
  ctx.translate(x, y);

  ctx.fillStyle = isDarkMode ? '#fff' : '#333';
  ctx.font = 'bold 11px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${Math.round(wind.speed * wind.gustFactor)} kts`, 0, size + 15);

  ctx.restore();
}

export function renderWindStreaks(
  ctx: CanvasRenderingContext2D,
  wind: WindState,
  width: number,
  height: number,
  timestamp: number
) {
  const streakCount = 15;
  const baseSpeed = wind.speed * wind.gustFactor;

  ctx.save();

  for (let i = 0; i < streakCount; i++) {
    const seed = i * 137.5;
    const baseX = ((seed * 7.3) % width);
    const baseY = ((seed * 11.7) % height);

    const time = timestamp * 0.001 * (0.3 + (i % 5) * 0.1);
    const moveX = (time * baseSpeed * 2) % (width + 200) - 100;
    const moveY = (time * baseSpeed * 2) % (height + 200) - 100;

    const x = (baseX + moveX * Math.sin(wind.direction)) % width;
    const y = (baseY - moveY * Math.cos(wind.direction)) % height;

    const length = 20 + (i % 3) * 10;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(
      x + Math.sin(wind.direction) * length,
      y - Math.cos(wind.direction) * length
    );

    ctx.strokeStyle = `rgba(255, 255, 255, ${0.05 + (i % 3) * 0.02})`;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.restore();
}
